/**
 * <club-1846-lead-form> — campaign lead modal loader.
 *
 * The form markup, validation and submit are NOT owned by the theme. On open we
 * fetch the campaign endpoint (settings.lead_campaign_url -> data-campaign-url),
 * which returns ready-made form HTML plus the WordPress plugin's own assets
 * (styles, scripts and globals). We inject the HTML into the modal, style it to
 * the theme look (component-lead-modal.css), then load the WP assets so the
 * plugin's own scripts validate the fields and submit the form. The theme only
 * provides the modal chrome, restyling and a best-effort Shopify email subscription.
 *
 * Flow on open:
 *   1. Open the modal + show a loading state.
 *   2. GET campaign endpoint -> inject payload.formHtml.
 *   3. Load payload.assets (globals first, then styles, then scripts in order)
 *      and nudge the WP orchestrator to bind to the freshly injected form.
 *
 * Validation, error/success messages and the POST to submitUrl are handled by the
 * WP scripts (form-validator.js / form-core.js / forms-orchestrator.js) using the
 * `bacaextFormsFrontValidation` / `bacaextFormsConfig` globals. The theme keeps a
 * single generic error only for the case where the campaign form fails to load.
 */
if (!customElements.get('club-1846-lead-form')) {
  // Asset presence is checked in the shared document rather than in module-local
  // state. This keeps deduplication working when two controller files are present
  // on the same page (newsletter + Dewar's Club 1846).
  const assetPresent = (tag, handle, src) => Array.from(document.querySelectorAll(tag))
    .some((element) => {
      const sameHandle = handle
        && element.getAttribute('data-bacaext-handle') === handle;
      const rawSrc = element.getAttribute('src') || element.getAttribute('href');
      const sameSrc = src && (rawSrc === src || element.src === src || element.href === src);
      return sameHandle || sameSrc;
    });

  customElements.define('club-1846-lead-form', class LeadForm extends HTMLElement {
    connectedCallback() {
      this.campaignUrl = this.dataset.campaignUrl;
      this.standalone = this.dataset.standalone === 'true';

      // Whether to also subscribe the email in Shopify (native customer form)
      // after a successful submit. Theme setting settings.lead_create_shopify_lead;
      // defaults to true, off only when explicitly set to "false".
      this.createShopifyLead = this.dataset.createShopifyLead !== 'false';

      // Debug logging: ?lead_debug=1 in the URL, or localStorage 'lead_debug'='1'.
      this.debug = /[?&]lead_debug=1/.test(window.location.search)
        || (window.localStorage && window.localStorage.getItem('lead_debug') === '1');

      // Static modal chrome / Shopify subscription nodes (outside the injected form).
      this.container = this.querySelector('[data-lead-form-container]');
      this.loadingEl = this.querySelector('[data-lead-loading]');
      this.titleEl = this.querySelector('[data-lead-title]');
      this.errorEl = this.querySelector('[data-lead-error]'); // generic load failure only
      this.dialog = this.querySelector('[data-lead-dialog]');
      this.shopifyForm = this.querySelector('[data-lead-shopify-form] form');
      this.shopifySink = this.querySelector('[data-lead-shopify-sink]');
      this.shopifyEmail = this.querySelector('[data-lead-shopify-email]');

      // Per-load state (reset on every open).
      this.formEl = null;
      this.pendingEmail = '';
      this.loading = false;
      // Email captured on the last submit attempt (used by the deferred Shopify
      // subscription once the WP POST is confirmed successful).
      this.lastEmail = '';
      // Guards handleSuccess() against firing twice for one submit; reset on open.
      this.successHandled = false;

      this.log('initialised', { campaignUrl: this.campaignUrl });

      // Delegate opening from any [data-open-club-1846-modal] trigger on the page.
      document.addEventListener('click', this.handleOpenTrigger.bind(this));

      // Vantage (Cocktails & Cie) has no Woolman modal runtime, so standalone
      // instances own close-button, backdrop and Escape-key handling.
      this.boundEsc = (event) => {
        if (event.key === 'Escape') this.closeModal();
      };
      if (this.standalone) {
        this.querySelectorAll('[data-lead-close]').forEach((element) => {
          element.addEventListener('click', (event) => {
            event.preventDefault();
            this.closeModal();
          });
        });
      }

      // Watch for the WP plugin's success modal so we can react theme-side
      // (subscribe to Shopify, restyle/decorate it, close our form modal).
      this.watchForSuccess();
    }

    log(...args) {
      if (this.debug) console.log('[lead]', ...args);
    }

    /**
     * Opens the modal from a trigger element, then loads the campaign form.
     * Supported trigger attributes:
     *   data-open-club-1846-modal          - marks the trigger
     *   data-title="..."              - overrides the modal title (optional)
     * If the trigger sits inside a form with an email field, that value is
     * pre-filled into the injected form once it arrives.
     */
    handleOpenTrigger(event) {
      const trigger = event.target.closest('[data-open-club-1846-modal]');
      if (!trigger) return;

      event.preventDefault();
      this.log('open trigger');

      // Allow a fresh success to be handled again on this re-open.
      this.successHandled = false;
      this.resetMessages();

      // Optional custom modal title per trigger.
      if (this.titleEl && trigger.dataset.title) {
        this.titleEl.textContent = trigger.dataset.title;
      }

      // Remember a sibling newsletter email to pre-fill once the form loads.
      const originForm = trigger.closest('form');
      const originEmail = originForm && originForm.querySelector('input[type="email"]');
      this.pendingEmail = (originEmail && originEmail.value) || '';

      // If the trigger lives inside a popup newsletter modal, close it first.
      const parentModal = trigger.closest('.modal[data-parent]');
      if (parentModal && parentModal !== this && window.Woolman?.ModalsAndDrawers) {
        window.Woolman.ModalsAndDrawers.closeModalOrDrawer(parentModal.id);
      }

      this.openModal();
      this.loadCampaign();
    }

    /** Opens through Woolman when available, otherwise uses standalone chrome. */
    openModal() {
      if (!this.standalone && window.Woolman?.ModalsAndDrawers) {
        window.Woolman.ModalsAndDrawers.showModalOrDrawer(this.id);
        return;
      }

      this.hidden = false;
      this.classList.add('is-open');
      document.documentElement.classList.add('lead-modal-open');
      document.body.classList.add('lead-modal-open');
      document.addEventListener('keydown', this.boundEsc);
      if (this.dialog) this.dialog.focus();
    }

    /** Closes through the same adapter that opened the modal. */
    closeModal() {
      if (!this.standalone && window.Woolman?.ModalsAndDrawers) {
        window.Woolman.ModalsAndDrawers.closeModalOrDrawer(this.id);
        return;
      }

      this.hidden = true;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('lead-modal-open');
      document.body.classList.remove('lead-modal-open');
      document.removeEventListener('keydown', this.boundEsc);
    }

    /** Success belongs only to the modal instance that is currently open. */
    isModalOpen() {
      return this.classList.contains('is-open');
    }

    /**
     * Fetches the campaign endpoint and injects its form. Runs on every open so
     * the one-time token (which expires) is always fresh. Guarded against
     * overlapping fetches.
     */
    loadCampaign() {
      if (this.loading) return;
      if (!this.campaignUrl) {
        this.log('no campaign URL configured -> showing error');
        this.showError();
        return;
      }

      this.loading = true;
      this.setLoadingState(true);

      fetch(this.campaignUrl, {
        method: 'GET',
        mode: 'cors',
        headers: { Accept: 'application/json' },
      })
        .then((res) => res.json())
        .then((json) => {
          const payload = json && json.payload;
          if (!json || json.success === false || !payload || !payload.formHtml) {
            throw new Error('campaign payload missing formHtml');
          }
          this.injectForm(payload);
        })
        .catch((err) => {
          this.log('campaign load failed -> showing error', err);
          this.showError();
        })
        .finally(() => {
          this.loading = false;
          this.setLoadingState(false);
        });
    }

    /**
     * Injects the fetched form HTML, restyles it to the theme, then loads the WP
     * assets that own validation + submit. The theme does NOT validate or submit.
     */
    injectForm(payload) {
      if (!this.container) return;
      this.log('campaign loaded', { submitUrl: payload.submitUrl, ageLimit: payload.ageLimit });

      // Trust boundary: formHtml comes from the campaign endpoint whose URL is a
      // theme setting controlled by the store admin (same trust level as the
      // OneTrust / age-gate scripts in settings_data) — not user input. We render
      // it verbatim because rendering the server's form is the feature.
      this.container.innerHTML = payload.formHtml;
      this.formEl = this.container.querySelector('form');
      if (!this.formEl) {
        this.showError();
        return;
      }

      // Title from the campaign (falls back to the locale default).
      const campaign = payload.campaign || {};
      if (this.titleEl && campaign.name) this.titleEl.textContent = campaign.name;

      // Pre-fill the email captured from the trigger's newsletter field.
      const emailInput = this.formEl.querySelector('input[type="email"], input[name="email"]');
      if (emailInput && this.pendingEmail) emailInput.value = this.pendingEmail;

      // Reuse the theme's button styling (brand colours come from the brand
      // stylesheet scoped to #modal-lead) instead of duplicating it in CSS.
      const submitBtn = this.formEl.querySelector('button[type="submit"], [type="submit"]');
      if (submitBtn) {
        const themeClasses = this.standalone
          ? ['primary-button', 'button']
          : ['btn', 'btn--primary', 'btn--full'];
        submitBtn.classList.add(...themeClasses, 'lead-form__submit');

        // Override the campaign's submit label with the Club 1846 CTA. The button
        // may be a <button> (textContent) or an <input type="submit"> (value).
        const submitLabel = 'Join Club 1846';
        if (submitBtn.tagName === 'INPUT') {
          submitBtn.value = submitLabel;
        } else {
          submitBtn.textContent = submitLabel;
        }
      }

      // Bring the campaign's native checkboxes in line with the theme's custom
      // .checkbox styling (critical.css), same approach as the submit button above.
      this.formEl
        .querySelectorAll('input[type="checkbox"]')
        .forEach((cb) => cb.classList.add('checkbox'));

      // Capture the entered email on every submit attempt (the form may be cleared
      // by the WP scripts on success). We do NOT subscribe to Shopify here — that
      // is deferred to handleSuccess(), fired only once the WP POST is confirmed
      // successful (its success modal appears). We never preventDefault; the WP
      // handler still validates and submits.
      this.formEl.addEventListener('submit', () => {
        const emailInput = this.formEl.querySelector('input[type="email"], input[name="email"]');
        if (emailInput && emailInput.value) this.lastEmail = emailInput.value;
      });

      // Hand validation + submit to the WP plugin's own scripts.
      this.loadAssets(payload.assets);
    }

    /**
     * Loads the WP plugin assets that drive validation + submit. Order matters:
     *   1. globals (bacaextFormsConfig / bacaextFormsFrontValidation) BEFORE scripts,
     *   2. styles,
     *   3. scripts sequentially in declared order (orchestrator depends on core).
     * Idempotent by handle/src so reopening the modal does not reload assets.
     */
    loadAssets(assets) {
      if (!assets) {
        this.log('no WP assets in payload');
        return;
      }

      // 1. Globals must exist before the WP scripts execute.
      if (assets.globals && typeof assets.globals === 'object') {
        Object.keys(assets.globals).forEach((key) => { window[key] = assets.globals[key]; });
      }

      // 2. Styles (idempotent).
      (assets.styles || []).forEach((style) => {
        if (!style.src || assetPresent('link', style.handle, style.src)) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = style.src;
        if (style.handle) link.setAttribute('data-bacaext-handle', style.handle);
        document.head.appendChild(link);
      });

      // 3. Scripts — sequential, order-preserving, idempotent.
      const scripts = assets.scripts || [];
      const loadNext = (i) => {
        if (i >= scripts.length) {
          this.initWpForms();
          return;
        }
        const script = scripts[i];
        if (!script.src || assetPresent('script', script.handle, script.src)) {
          loadNext(i + 1);
          return;
        }
        const el = document.createElement('script');
        el.src = script.src;
        if (script.handle) el.setAttribute('data-bacaext-handle', script.handle);
        el.onload = () => loadNext(i + 1);
        el.onerror = () => {
          this.log('WP asset failed to load', script.src);
          loadNext(i + 1);
        };
        document.head.appendChild(el);
      };
      loadNext(0);
    }

    /**
     * Hands the freshly injected campaign form to the WP forms orchestrator by
     * dispatching the exact event it listens for. The orchestrator self-initialises
     * synchronously on script load and (re)binds forms on the `document` event
     * 'bacaext:modal:content-updated', whose detail.modal is a DOM element it scopes
     * bootstrapForms() to. Binding the campaign controller is what activates the
     * plugin's own reCAPTCHA flow: form-core reads data-bacaext-recaptcha-required /
     * data-bacaext-recaptcha-site-key off the form, loads
     * bacaextFormsConfig.recaptcha.scriptUrl (render=explicit), renders the widget
     * into the server-provided [data-bacaext-recaptcha="1"] .bacaext-recaptcha-widget
     * slot, injects the g-recaptcha-response token and blocks submit until it is
     * solved. The theme does NOT render reCAPTCHA itself. Re-dispatching on every
     * open is safe: the orchestrator guards binding with a WeakMap (an already-bound
     * form is updated, not re-rendered).
     */
    initWpForms() {
      // Scope must contain the form: bootstrapForms() uses querySelectorAll
      // (descendants only). The injected form is a child of the container; fall
      // back to the <club-1846-lead-form> host, which also contains it.
      const scope = this.container || this;
      document.dispatchEvent(
        new CustomEvent('bacaext:modal:content-updated', { detail: { modal: scope } })
      );
      this.log('WP orchestrator notified (bacaext:modal:content-updated)', scope);
    }

    /**
     * Watches the document for the WP plugin's success modal. The plugin owns the
     * POST and, on a successful submit, appends its own modal to <body>:
     *   <div class="bacaext-modal is-open">…<div class="bacaext-modal__dialog is-success">…
     * That `.is-success` dialog is the only theme-visible signal that the POST
     * succeeded (the WP scripts and their internal events are not in the theme).
     * On detection we: (1) subscribe the email to Shopify — deferred until now so
     * it never fires on a failed/abandoned submit; (2) decorate the WP modal with
     * our logo (it ships an empty .bacaext-modal__logo); (3) close our own form
     * modal so it is not left open behind the success overlay.
     */
    watchForSuccess() {
      const findSuccess = (node) => {
        if (!node || node.nodeType !== 1) return null;
        if (node.matches && node.matches('.bacaext-modal')
          && node.querySelector('.bacaext-modal__dialog.is-success')) {
          return node;
        }
        if (node.querySelector) {
          const dialog = node.querySelector('.bacaext-modal .bacaext-modal__dialog.is-success');
          if (dialog) return dialog.closest('.bacaext-modal');
        }
        return null;
      };

      const observer = new MutationObserver((mutations) => {
        if (this.successHandled) return;
        if (!this.isModalOpen()) return;
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            const modal = findSuccess(node);
            if (modal) {
              this.handleSuccess(modal);
              return;
            }
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    /** Runs once when the WP success modal appears. */
    handleSuccess(modalEl) {
      if (this.successHandled) return;
      this.successHandled = true;
      this.log('WP success modal detected', modalEl);

      // 1. Now that the POST is confirmed, capture the email in Shopify.
      this.subscribeShopify();

      // 2. Give the WP modal our brand logo (it ships an empty logo slot).
      this.decorateSuccessModal(modalEl);

      // 3. Close our own form modal so it is not left open behind the success view.
      this.closeModal();

      // 4. Two independent modals (ours + the WP success modal) both lock body
      // scroll on the same <body>. Ours is released above, but the WP modal keeps
      // its own lock and does not reliably restore scroll on close. Make sure
      // scrolling works again once the WP success modal is dismissed.
      this.restoreScrollOnSuccessClose(modalEl);
    }

    /**
     * Ensures body scrolling is restored after the WP success modal is dismissed
     * (close button, backdrop click, or node removal). The theme locks scroll via
     * the global bodyScrollLock; the WP modal may also set inline overflow. We
     * clear both, once, after the modal goes away. Safe to clear all locks here:
     * our own modal is already closed, so nothing else should be holding one.
     */
    restoreScrollOnSuccessClose(modalEl) {
      const lockClasses = [
        'overflow-hidden', 'no-scroll', 'modal-open',
        'bacaext-modal-open', 'bacaext-no-scroll', 'lead-modal-open',
      ];

      const restore = () => {
        if (window.bodyScrollLock?.clearAllBodyScrollLocks) {
          window.bodyScrollLock.clearAllBodyScrollLocks();
        }
        [document.body, document.documentElement].forEach((el) => {
          const s = el.style;
          if (s.overflow === 'hidden' || s.overflow === 'clip') s.overflow = '';
          if (s.overflowY === 'hidden' || s.overflowY === 'clip') s.overflowY = '';
          // A position:fixed lock parks the page scroll offset in `top`; undo it
          // and restore the offset so the page does not jump to the top.
          if (s.position === 'fixed') {
            const offset = parseInt(s.top, 10) || 0;
            s.position = '';
            s.top = '';
            s.left = '';
            s.width = '';
            if (offset) window.scrollTo(0, -offset);
          }
          s.removeProperty('padding-right');
          lockClasses.forEach((c) => el.classList.remove(c));
        });
        this.log('body scroll restored after success modal close');
      };

      // restore() is idempotent, so run it now and again on the next frames to
      // out-race a late WP teardown that may re-lock after removing the node.
      const restoreDeferred = () => {
        restore();
        requestAnimationFrame(restore);
        setTimeout(restore, 120);
      };

      let done = false;
      const removalObserver = new MutationObserver(() => {
        if (done || document.body.contains(modalEl)) return;
        done = true;
        removalObserver.disconnect();
        restoreDeferred();
      });
      removalObserver.observe(document.body, { childList: true, subtree: true });

      // Belt-and-suspenders for close paths that don't remove the node.
      const closeBtn = modalEl.querySelector('.bacaext-modal__close');
      if (closeBtn) closeBtn.addEventListener('click', restoreDeferred);
      const backdrop = modalEl.querySelector('.bacaext-modal__backdrop');
      if (backdrop) backdrop.addEventListener('click', restoreDeferred);
    }

    /**
     * Puts our brand logo (rendered from settings.lead_modal_logo in
     * lead-modal.liquid) inside the WP success card, above the title, so the
     * success view shows the same logo in the same place as the form. The WP
     * modal ships an empty .bacaext-modal__logo *outside* the dialog; we prepend
     * a .lead-modal__logo into the dialog instead (so it sits inside the card and
     * reuses the theme logo styling) and let CSS hide the empty WP slot.
     * No-op if the source logo or the dialog is missing.
     */
    decorateSuccessModal(modalEl) {
      const dialog = modalEl && modalEl.querySelector('.bacaext-modal__dialog');
      if (!dialog || dialog.querySelector('.lead-modal__logo')) return;
      const sourceImg = this.querySelector('.lead-modal__logo img');
      if (!sourceImg) return;
      const logo = document.createElement('div');
      logo.className = 'lead-modal__logo';
      logo.appendChild(sourceImg.cloneNode(true));
      dialog.insertBefore(logo, dialog.firstChild);
    }

    /**
     * Subscribes the entered email to Shopify email marketing in the background.
     * Shopify protects /contact with hCaptcha — a POST without a valid token is
     * rejected. We wire up the token via window.Shopify.captcha.protect, then
     * submit the native customer form into a hidden iframe (no navigation). The
     * customer is created/updated with marketing consent = subscribed. Decoupled
     * from the campaign endpoint and best effort.
     */
    subscribeShopify() {
      // Off via theme setting (settings.lead_create_shopify_lead = false): skip
      // the native Shopify subscription entirely.
      if (!this.createShopifyLead) {
        this.log('Shopify lead creation disabled by setting — skipping');
        return;
      }
      if (!this.shopifyForm) return;
      // Prefer the email captured on submit; fall back to the live form field
      // (the WP scripts may have cleared the form by the time success fires).
      const liveInput = this.formEl
        && this.formEl.querySelector('input[type="email"], input[name="email"]');
      const email = this.lastEmail || (liveInput && liveInput.value) || '';
      if (!email) return;
      if (this.shopifyEmail) this.shopifyEmail.value = email;
      if (this.shopifySink?.name) this.shopifyForm.target = this.shopifySink.name;

      const send = () => {
        this.log('subscribing email to Shopify ->', this.shopifyForm.action);
        this.shopifyForm.submit();
      };

      if (window.Shopify && window.Shopify.captcha && window.Shopify.captcha.protect) {
        window.Shopify.captcha.protect(this.shopifyForm, send);
      } else {
        send();
      }
    }

    /** Shows/hides the loading indicator and hides the form container while loading. */
    setLoadingState(isLoading) {
      if (this.loadingEl) this.loadingEl.hidden = !isLoading;
      if (this.container) this.container.hidden = isLoading;
    }

    /** Generic error for when the campaign form itself fails to load. */
    showError() {
      if (this.errorEl) this.errorEl.hidden = false;
    }

    resetMessages() {
      if (this.errorEl) this.errorEl.hidden = true;
      if (this.container) this.container.hidden = false;
    }
  });
}
